
import React from "react"
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Grid
} from "@material-ui/core"

import { IProductMessageMetrics } from "@/protocols/metrics"
import { MessageFeature } from "@/protocols/channel"

import { Portlet } from "@/components"

import useCustomStyles from "@/styles/custom"
import useStyles from "@/pages/Admin/Dashboard/Metrics/ProductMessageMetrics/LastDaysMetrics/LastDaysMetricsTableCard/styles"

import {
	isYesterday,
	isToday,
	getBrazilianWeekDay,
	formatDateInBrazilianDate,
	getDateWithBrazilianTimezone
} from "@/utils/time"
import ProductMessageMetricsSkeleton from "@/skeletons/Admin/ProductMessageMetricsSkeleton"

export type LastDaysMetricsFilter = {
	lastDaysCount: number
	features: MessageFeature[]
}

export type FilterOptions = {
	features: Array<{
		value: MessageFeature[]
		title: string
	}>
	lastDays: Array<{
		value: number
		title: string
	}>
}

type LastDaysMetricsTableCardProps = {
	messageMetricsByDate: IProductMessageMetrics["messageMetricsByDate"]
	lastDaysMetricsFilter: LastDaysMetricsFilter
	loading: boolean
}

const LastDaysMetricsTableCard: React.FC<LastDaysMetricsTableCardProps> = (props) => {
	const {
		messageMetricsByDate,
		lastDaysMetricsFilter,
		loading = false
	} = props

	const customClasses = useCustomStyles()
	const classes = useStyles()

	const getFormattedDate = (dateInString: string) => {
		/**
		 * WARNING:
		 * - Since 'messageMetricByDate.date' is returned from the backend with Brazilian timezone,
		 * we need to explicitly convert it to a Date using the same timezone. This prevents
		 * conversion issues for users in different timezones.
		 */
		const date = getDateWithBrazilianTimezone(dateInString)

		const isYesterdayDate = isYesterday(date)
		const isTodayDate = isToday(date)

		const dateParams = []

		if (isTodayDate) {
			dateParams.push("Hoje")
		} else if (isYesterdayDate) {
			dateParams.push("Ontem")
		} else {
			dateParams.push(getBrazilianWeekDay(date))
		}

		dateParams.unshift(formatDateInBrazilianDate(date))

		const formattedDate = dateParams.join(" - ")

		return formattedDate
	}

	return (
		<Portlet>
			<Grid
				container
				spacing={2}
			>
				<Grid
					item
					xs={12}
				>
					<Grid
						container
						spacing={2}
					>
						<Grid
							item
							xs={12}
							md={3}
						>
							<Typography
								variant="h2"
								className={customClasses.uppercase}
							>
								Últimos {lastDaysMetricsFilter.lastDaysCount} dias
							</Typography>
						</Grid>
					</Grid>
				</Grid>

				<Grid
					item
					xs={12}
				>
					<Grid
						container
					>
						<TableContainer>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										<TableCell>
											DATA
										</TableCell>

										<TableCell>
											ENVIADAS E NÃO ENTREGUES
										</TableCell>

										<TableCell>
											ENTREGUES
										</TableCell>

										<TableCell>
											COM ERRO
										</TableCell>
									</TableRow>
								</TableHead>

								<TableBody className={customClasses.reportTableBodyText}>
									{loading
										? <ProductMessageMetricsSkeleton rows={lastDaysMetricsFilter.lastDaysCount} />
										: messageMetricsByDate?.map(messageMetricByDate => (
											<TableRow key={messageMetricByDate.date} tabIndex={-1}>
												<TableCell className={classes.date}>
													{getFormattedDate(messageMetricByDate.date)}
												</TableCell>
												<TableCell>{messageMetricByDate.sentButNotDeliveredMessages || 0}</TableCell>
												<TableCell>{messageMetricByDate.deliveredMessages || 0}</TableCell>
												<TableCell>{messageMetricByDate.notSentMessages || 0}</TableCell>
											</TableRow>
										))
									}
								</TableBody>

							</Table>
						</TableContainer>
					</Grid>
				</Grid>
			</Grid>
		</Portlet>
	)
}

export default LastDaysMetricsTableCard
